import { useEffect, useState } from "react";
import { api, fmtDate, formatErr, canWrite } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ExportPdfButton } from "@/components/ExportPdfButton";
import { useCompany } from "@/contexts/CompanyContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { MonthPicker } from "@/components/MonthPicker";
import { PadroncinoSelect, padrLabel } from "@/components/PadroncinoSelect";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, Trash2, Pencil, Bell, Mail, MessageCircle, CheckCheck, Truck, Search, HardHat } from "lucide-react";
import { toast } from "sonner";

const TIPI = [
  { value: "assicurazione", label: "Assicurazione" },
  { value: "revisione", label: "Revisione" },
  { value: "tachigrafo", label: "Tachigrafo" },
  { value: "patente", label: "Patente" },
  { value: "carta_tachigrafica", label: "Carta tachigrafica" },
  { value: "altro", label: "Altro" },
];
const TIPO_LABEL = { documento: "Documento azienda" };
const TABS = [
  { value: "tutte", label: "Tutte" }, { value: "assicurazione", label: "Assicurazioni" },
  { value: "revisione", label: "Revisioni" }, { value: "tachigrafo", label: "Tachigrafi" }, { value: "altre", label: "Altre" },
];
const VEIC_TIPI = ["assicurazione", "revisione", "tachigrafo"];
export const CANALI = { email: "Email", whatsapp: "WhatsApp", manuale: "Segnato a mano" };

export const daysUntil = (d) => Math.ceil((new Date(d + "T00:00:00") - new Date(new Date().toDateString())) / 86400000);
export const stato = (d) => {
  const n = daysUntil(d);
  if (n < 0) return { cls: "bg-slate-600", txt: `Scaduta da ${-n}gg` };
  if (n <= 15) return { cls: "bg-red-600", txt: `${n}gg` };
  if (n <= 30) return { cls: "bg-orange-500", txt: `${n}gg` };
  if (n <= 60) return { cls: "bg-yellow-500", txt: `${n}gg` };
  return { cls: "bg-green-600", txt: `${n}gg` };
};

export default function Scadenzario() {
  const { company } = useCompany();
  const { user } = useAuth();
  const writable = canWrite(user, "scadenze");
  const [editId, setEditId] = useState(null);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [veicoli, setVeicoli] = useState([]);
  const [padroncini, setPadroncini] = useState([]);
  const [mese, setMese] = useState("");
  const [tab, setTab] = useState("tutte");
  const [q, setQ] = useState("");
  const [soloVicine, setSoloVicine] = useState(false);

  const load = async () => {
    const { data } = await api.get("/scadenze", { params: { company } });
    setItems(data.sort((a, b) => a.data_scadenza.localeCompare(b.data_scadenza)));
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [company]);
  useEffect(() => {
    api.get("/veicoli", { params: { company } }).then(r => setVeicoli(r.data));
    api.get("/padroncini", { params: { company } }).then(r => setPadroncini(r.data));
  }, [company]);

  const targa = (s) => veicoli.find(v => v.id === s.veicolo_id)?.targa;
  const padr = (s) => padroncini.find(p => p.id === s.padroncino_id);
  const inTab = (s, t) => t === "tutte" || (t === "altre" ? !VEIC_TIPI.includes(s.tipo) : s.tipo === t);
  const base = items.filter(s => (!mese || s.data_scadenza?.startsWith(mese)) && (!soloVicine || daysUntil(s.data_scadenza) <= 30));
  const term = q.trim().toLowerCase();
  const shown = base.filter(s => inTab(s, tab) && (!term ||
    `${s.descrizione} ${targa(s) || ""} ${padrLabel(padr(s))} ${s.broker || ""} ${s.compagnia || ""}`.toLowerCase().includes(term)));
  const urgenti = items.filter(s => daysUntil(s.data_scadenza) <= 30).length;

  const openNew = () => { setEditId(null); setForm({ company: company === "all" ? "golmar" : company, tipo: "altro" }); setOpen(true); };
  const openEdit = (s) => { setEditId(s.id); setForm({ ...s }); setOpen(true); };
  const save = async () => {
    try {
      if (editId) await api.patch(`/scadenze/${editId}`, form); else await api.post("/scadenze", form);
      toast.success(editId ? "Scadenza aggiornata" : "Scadenza aggiunta"); setOpen(false); load();
    }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Elimina?")) return;
    await api.delete(`/scadenze/${id}`); load(); };
  const avvisa = (s, canale) => avvisaScadenza(s, canale, load);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-heading font-black">Scadenzario</h1>
          <div className="text-sm text-muted-foreground">Assicurazioni, revisioni e tachigrafi arrivano in automatico dalle date inserite nei veicoli.</div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <MonthPicker value={mese} onChange={setMese} allowClear testId="scad-mese" />
          <ExportPdfButton resource="scadenze" params={{ company }} />
          {writable && <Button size="sm" onClick={openNew} className="h-8 gap-1" data-testid="scad-new-button"><Plus className="w-4 h-4" />Nuova scadenza</Button>}
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>{TABS.map(t => (
            <TabsTrigger key={t.value} value={t.value} data-testid={`scad-tab-${t.value}`}>
              {t.label} ({base.filter(s => inTab(s, t.value)).length})
            </TabsTrigger>))}
          </TabsList>
        </Tabs>
        <div className="relative w-64">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca targa, padroncino, broker..." className="h-8 pl-7" data-testid="scad-search" />
        </div>
        <Button size="sm" variant={soloVicine ? "default" : "outline"} className="h-8 gap-1" onClick={() => setSoloVicine(!soloVicine)} data-testid="scad-urgenti-toggle">
          <Bell className="w-3.5 h-3.5" />Entro 30 giorni ({urgenti})
        </Button>
        <Legend />
      </div>

      <Card className="rounded-md overflow-x-auto">
        <table className="autotrans-table w-full" data-testid="scad-table">
          <thead><tr>
            <th>Tipo</th><th>Descrizione</th><th>Scadenza</th><th>Mancano</th><th>Veicolo</th><th>Padroncino</th>
            {(tab === "assicurazione" || tab === "tutte") && <><th>Broker</th><th>Compagnia</th></>}
            <th>Ultimo avviso</th><th className="w-36">Azioni</th>
          </tr></thead>
          <tbody>
            {shown.length === 0 && <tr><td colSpan={10} className="text-center py-6 text-muted-foreground">Nessuna scadenza</td></tr>}
            {shown.map((s) => <Riga key={s.id} s={s} tab={tab} targa={targa(s)} padr={padr(s)} writable={writable}
                                    onEdit={openEdit} onDel={del} onAvvisa={avvisa} />)}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editId ? "Modifica scadenza" : "Nuova scadenza"}</DialogTitle></DialogHeader>
          <div className="text-xs text-muted-foreground -mt-2">Per assicurazione, revisione e tachigrafo dei mezzi basta inserire la data nella scheda del veicolo.</div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Azienda"><Select value={form.company} onValueChange={(v) => setForm({ ...form, company: v })}>
              <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
              <SelectContent>{["golmar","vitam","rotta66","fmg"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select></F>
            <F label="Tipo"><Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
              <SelectTrigger className="h-8" data-testid="scad-field-tipo"><SelectValue /></SelectTrigger>
              <SelectContent>{TIPI.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select></F>
            <F label="Descrizione"><Input className="h-8" value={form.descrizione || ""} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} data-testid="scad-field-descrizione" /></F>
            <F label="Data scadenza"><Input type="date" className="h-8" value={form.data_scadenza || ""} onChange={(e) => setForm({ ...form, data_scadenza: e.target.value })} data-testid="scad-field-data" /></F>
            <F label="Veicolo"><Select value={form.veicolo_id || ""} onValueChange={(v) => setForm({ ...form, veicolo_id: v })}>
              <SelectTrigger className="h-8"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{veicoli.map(v => <SelectItem key={v.id} value={v.id}>{v.targa}</SelectItem>)}</SelectContent>
            </Select></F>
            <F label="Padroncino"><PadroncinoSelect value={form.padroncino_id || ""} onChange={(v) => setForm({ ...form, padroncino_id: v || null })} allowEmpty
                    padroncini={padroncini.filter(p => p.company === form.company)} placeholder="—" className="h-8 w-full" testId="scad-field-padroncino" /></F>
            {form.tipo === "assicurazione" && <F label="Broker"><Input className="h-8" value={form.broker || ""} onChange={(e) => setForm({ ...form, broker: e.target.value })} data-testid="scad-field-broker" /></F>}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button><Button onClick={save} data-testid="scad-save-button">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Riga = ({ s, tab, targa, padr, writable, onEdit, onDel, onAvvisa }) => {
  const st = stato(s.data_scadenza);
  const av = s.ultimo_avviso;
  return (
    <tr data-testid={`scad-row-${s.id}`}>
      <td className="whitespace-nowrap"><Badge variant="outline" className="h-5">{TIPI.find(t => t.value === s.tipo)?.label || TIPO_LABEL[s.tipo] || s.tipo}</Badge>
        {s.auto && !s.documento_id && <Badge variant="secondary" className="h-5 ml-1 gap-1" title={s.dipendente_id ? "Data presa dalla scheda dipendente" : "Data presa dalla scheda veicolo"}>{s.dipendente_id ? <HardHat className="w-3 h-3" /> : <Truck className="w-3 h-3" />}{s.dipendente_id ? "Dipendente" : "Veicolo"}</Badge>}</td>
      <td>{s.descrizione}</td>
      <td className="font-mono">{fmtDate(s.data_scadenza)}</td>
      <td><Badge className={`${st.cls} text-white h-5 whitespace-nowrap`} data-testid={`scad-days-${s.id}`}>{st.txt}</Badge></td>
      <td className="font-mono text-xs">{targa || "—"}</td>
      <td className="text-xs">{padrLabel(padr) || "—"}</td>
      {(tab === "assicurazione" || tab === "tutte") && <>
        <td className="text-xs" data-testid={`scad-broker-${s.id}`}>{s.tipo === "assicurazione" ? (s.broker || <span className="text-orange-600">da indicare</span>) : ""}</td>
        <td className="text-xs">{s.tipo === "assicurazione" ? (s.compagnia || "—") : ""}</td>
      </>}
      <td className="text-xs whitespace-nowrap" data-testid={`scad-avviso-${s.id}`}>
        {av ? <span className="text-green-700 dark:text-green-400">{CANALI[av.canale]} · {fmtDate(av.data)}</span> : <span className="text-muted-foreground">mai</span>}
      </td>
      <td className="whitespace-nowrap">{writable && <>
        <AvvisaMenu s={s} onAvvisa={onAvvisa} />
        {!s.auto && <>
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onEdit(s)} data-testid={`scad-edit-${s.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
          <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => onDel(s.id)} data-testid={`scad-delete-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
        </>}
      </>}</td>
    </tr>
  );
};

const Legend = () => (
  <div className="flex items-center gap-2 text-xs text-muted-foreground ml-auto" data-testid="scad-legend">
    {[["bg-red-600", "≤15gg"], ["bg-orange-500", "≤30gg"], ["bg-yellow-500", "≤60gg"], ["bg-green-600", "oltre"], ["bg-slate-600", "scaduta"]].map(([c, l]) => (
      <span key={l} className="flex items-center gap-1"><span className={`w-2.5 h-2.5 rounded-full ${c}`} />{l}</span>
    ))}
  </div>
);

const F = ({ label, children }) => <div><Label className="text-xs font-heading uppercase tracking-wider">{label}</Label><div className="mt-1">{children}</div></div>;

export const avvisaScadenza = async (s, canale, done) => {
  try {
    const { data } = await api.post(`/scadenze/${s.id}/avvisa`, { canale });
    toast.success(canale === "manuale" ? "Segnato come avvisato" : `Avviso inviato via ${CANALI[canale]} a ${data.destinatario}`);
  } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  done();
};

export const AvvisaMenu = ({ s, onAvvisa, prefix = "scad" }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button size="sm" variant="outline" className="h-7 gap-1 px-2" data-testid={`${prefix}-avvisa-${s.id}`}><Bell className="w-3.5 h-3.5" />Avvisa</Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end">
      {s.padroncino_id && <>
      <DropdownMenuItem onClick={() => onAvvisa(s, "email")} data-testid={`${prefix}-avvisa-email-${s.id}`}><Mail className="w-4 h-4 mr-2" />Invia email</DropdownMenuItem>
      <DropdownMenuItem onClick={() => onAvvisa(s, "whatsapp")} data-testid={`${prefix}-avvisa-whatsapp-${s.id}`}><MessageCircle className="w-4 h-4 mr-2" />Invia WhatsApp</DropdownMenuItem>
      </>}
      <DropdownMenuItem onClick={() => onAvvisa(s, "manuale")} data-testid={`${prefix}-avvisa-manuale-${s.id}`}><CheckCheck className="w-4 h-4 mr-2" />Segna come avvisato</DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);
